import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, Sparkles, XCircle, Settings2, Trash2, Bell, Check, Download, Upload, AlertTriangle, Wrench } from "lucide-react";
import { toast } from "sonner";
import { CourseBuilder } from "./CourseBuilderAdmin";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]";
const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5";
const btnPrimary = { background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" };

const api = async (method, path, body) => {
  const r = await fetch(`${API}/api/admin/lms/${path}`, {
    method,
    credentials: "include",
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Request failed");
  return r.json();
};

/* ---- YouTube whitelist manager ---- */
function WhitelistManager() {
  const [channels, setChannels] = useState(null);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [recs, setRecs] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  const [addingRec, setAddingRec] = useState({});

  const loadChannels = () => api("GET","youtube/whitelist").then(setChannels).catch(()=>setChannels([]));
  useEffect(() => { loadChannels(); }, []);

  const add = async () => {
    const val = newUrl.trim();
    if (!val) return;
    setAdding(true);
    try {
      await api("POST","youtube/whitelist",{url: val});
      setNewUrl("");
      loadChannels();
      toast.success("Channel added");
    } catch(e){ toast.error(e.message); }
    finally{ setAdding(false); }
  };

  const rm = async(id) => {
    await api("DELETE",`youtube/whitelist/${id}`);
    loadChannels();
    toast.success("Removed");
  };

  const findRecommendations = async () => {
    setRecsLoading(true);
    try {
      const d = await api("GET","youtube/recommend");
      setRecs(d.recommendations || []);
      if ((d.recommendations || []).length === 0) toast.info("No new recommendations found");
    } catch(e){ toast.error(e.message); }
    finally{ setRecsLoading(false); }
  };

  const addRec = async (rec) => {
    setAddingRec(prev => ({...prev, [rec.channel_id]: true}));
    try {
      await api("POST","youtube/whitelist",{
        name: rec.name, handle: rec.handle, channel_id: rec.channel_id
      });
      loadChannels();
      setRecs(prev => (prev||[]).filter(r => r.channel_id !== rec.channel_id));
      toast.success(`Added ${rec.name}`);
    } catch(e){ toast.error(e.message); }
    finally{ setAddingRec(prev => ({...prev, [rec.channel_id]: false})); }
  };

  const fmtSubs = (n) => n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(0)}K` : n;

  if(!channels) return null;
  return (
    <div className="rp-foam bg-white/55 p-5 md:p-6 mt-5" data-testid="whitelist-manager">
      <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium mb-3">Approved YouTube Channels ({channels.length})</p>
      <div className="flex gap-2 mb-3">
        <input value={newUrl} onChange={e=>setNewUrl(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()}
               placeholder="YouTube URL, @handle, or channel name" className={inputCls} data-testid="whitelist-input" />
        <button onClick={add} disabled={adding} className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary} data-testid="whitelist-add-btn">
          <Plus size={14} /> {adding ? "Adding…" : "Add"}
        </button>
      </div>
      <div className="flex gap-2 mb-3">
        <button onClick={findRecommendations} disabled={recsLoading} data-testid="whitelist-recommend-btn"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1.5 hover:bg-[#B5793F12] disabled:opacity-50">
          <Sparkles size={11} /> {recsLoading ? "Searching…" : "Find new channel recommendations"}
        </button>
      </div>

      {/* Recommendations */}
      {recs && recs.length > 0 && (
        <div className="mb-4 border border-[#B5793F33] rounded-xl p-3 bg-[#B5793F08]" data-testid="whitelist-recommendations">
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#B5793F] font-semibold mb-2">Recommended Channels ({recs.length})</p>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {recs.map(r => (
              <div key={r.channel_id} className="flex items-center gap-3 py-2 px-2 rounded-lg bg-white/70 text-sm" data-testid={`rec-channel-${r.channel_id}`}>
                {r.thumbnail && <img src={r.thumbnail} alt="" className="w-8 h-8 rounded-full shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#262016] truncate">{r.name}</p>
                  <p className="text-[11px] text-[#8A7E6B] truncate">{r.handle} · {fmtSubs(r.subscribers)} subscribers</p>
                </div>
                <button onClick={()=>addRec(r)} disabled={addingRec[r.channel_id]}
                        className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}
                        data-testid={`rec-add-${r.channel_id}`}>
                  <Plus size={10} /> {addingRec[r.channel_id] ? "…" : "Add"}
                </button>
              </div>
            ))}
          </div>
          <button onClick={()=>setRecs(null)} className="mt-2 text-[11px] text-[#8A7E6B] underline">Dismiss</button>
        </div>
      )}

      <div className="max-h-48 overflow-y-auto space-y-2">
        {channels.map(c=>(
          <div key={c.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[#26201608] text-sm text-[#262016]">
            <span className="flex-1 min-w-0 truncate">{c.name||c.handle||c.channel_name||c.channel_url||c.channel_id||"Unknown channel"}</span>
            {c.handle && <span className="text-[11px] text-[#8A7E6B] mr-2 shrink-0">{c.handle}</span>}
            <button onClick={()=>rm(c.id)} className="text-xs text-[#8A7E6B] hover:text-red-700 ml-2 shrink-0" data-testid={`whitelist-rm-${c.id}`}>remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Content refresh panel ---- */
function ContentRefreshPanel() {
  const [drafts, setDrafts] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const loadDrafts = useCallback(() => {
    fetch(`${API}/api/admin/lms/refresh/drafts`,{credentials:"include"}).then(r=>r.ok?r.json():[]).then(setDrafts).catch(()=>setDrafts([]));
  }, []);
  useEffect(()=>{loadDrafts();},[loadDrafts]);
  const trigger = async () => {
    setTriggering(true);
    try {
      const r = await fetch(`${API}/api/admin/lms/refresh/trigger`,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include"});
      if(!r.ok) throw new Error((await r.json()).detail||"Failed");
      toast.success("Content scan started — drafts will appear in a few minutes");
      setTimeout(loadDrafts,30000);
    } catch(e){toast.error(e.message);}
    finally{setTriggering(false);}
  };
  const approveDraft = async(id)=>{const r=await fetch(`${API}/api/admin/lms/refresh/drafts/${id}/approve`,{method:"POST",credentials:"include"});if(r.ok){toast.success("Lesson added");loadDrafts();}else toast.error("Approve failed");};
  const dismissDraft = async(id)=>{const r=await fetch(`${API}/api/admin/lms/refresh/drafts/${id}/dismiss`,{method:"POST",credentials:"include"});if(r.ok){toast.success("Dismissed");loadDrafts();}else toast.error("Dismiss failed");};
  const pendingCount=(drafts||[]).length;
  return (
    <div className="rp-foam bg-white/55 p-5 md:p-6" data-testid="content-refresh-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <RefreshCw size={16} className="text-[#B5793F]" />
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium">Weekly Content Refresh</p>
          {pendingCount>0&&<span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#B5793F] text-[10px] font-bold text-white">{pendingCount}</span>}
        </div>
        <button onClick={trigger} disabled={triggering} data-testid="refresh-trigger-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold border border-[#B5793F55] text-[#B5793F] hover:bg-[#B5793F12] disabled:opacity-50">
          <RefreshCw size={13} className={triggering?"animate-spin":""} /> {triggering?"Scanning…":"Scan now"}
        </button>
      </div>
      {pendingCount>0&&(
        <div className="space-y-5" data-testid="refresh-drafts">
          {drafts.map(d=>(
            <div key={d.id} className="flex gap-3 p-3 rounded-lg border border-[#26201618] bg-white/70" data-testid={`draft-${d.id}`}>
              {(d.video_thumbnail||d.thumbnail)&&<img src={d.video_thumbnail||d.thumbnail} alt="" className="w-20 h-14 rounded object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#262016] line-clamp-1">{d.video_title||d.suggested_lesson_title||d.title||"Untitled"}</p>
                <p className="text-xs text-[#8A7E6B] mt-0.5">{d.video_channel||d.channel||"Unknown channel"} · {d.source_type||"video"}</p>
                {d.suggested_lesson_title&&d.video_title&&<p className="text-[11px] text-[#B5793F] mt-0.5">→ Lesson: {d.suggested_lesson_title}</p>}
                {(d.suggested_description||d.ai_description)&&<p className="text-xs text-[#5C5042] mt-1 line-clamp-2">{d.suggested_description||d.ai_description}</p>}
                {d.video_url&&<a href={d.video_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#B5793F] hover:underline mt-1 inline-block">Watch video ↗</a>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={()=>approveDraft(d.id)} data-testid={`draft-approve-${d.id}`} className="px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#F6F1E5]" style={btnPrimary}><Check size={11} className="inline mr-1" />Approve</button>
                <button onClick={()=>dismissDraft(d.id)} data-testid={`draft-dismiss-${d.id}`} className="px-3 py-1.5 rounded-full text-[11px] font-medium border border-[#26201620] text-[#8A7E6B] hover:text-red-700">Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {pendingCount===0&&<p className="text-xs text-[#8A7E6B]">No pending suggestions. Content is scanned every Friday.</p>}
    </div>
  );
}

/* ---- Main AdminCourses component ---- */
export default function AdminCourses() {
  const [courses, setCourses] = useState(null);
  const [openCourse, setOpenCourse] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBuilding, setAiBuilding] = useState(false);
  const [aiProgress, setAiProgress] = useState("");
  const [showWhitelist, setShowWhitelist] = useState(false);
  const [bulkAllGenning, setBulkAllGenning] = useState(false);
  const [bulkAllProgress, setBulkAllProgress] = useState("");
  const [bulkJob, setBulkJob] = useState(null);
  const pollRef = React.useRef(null);
  const [recoveryStatus, setRecoveryStatus] = useState(null);
  const recoveryPollRef = React.useRef(null);
  const [scopedFill, setScopedFill] = useState(null);
  const scopedPollRef = React.useRef(null);

  const load = useCallback(() => { api("GET", "modules").then(setCourses).catch(() => setCourses([])); }, []);
  useEffect(() => { load(); }, [load]);

  // Poll for bulk fill job status
  const pollStatus = useCallback(async () => {
    try {
      const job = await api("GET", "bulk-fill/status");
      setBulkJob(job);
      if (job.status === "running") {
        setBulkAllGenning(true);
        setBulkAllProgress(job.current || `Processed ${job.processed || 0}/${job.total || "?"}`);
      } else {
        setBulkAllGenning(false);
        if (job.status === "done" || job.status === "cancelled") {
          setBulkAllProgress("");
          load();
        } else if (job.status === "error") {
          setBulkAllProgress("");
        }
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch { /* ignore */ }
  }, [load]);

  useEffect(() => { pollStatus(); }, [pollStatus]);

  const startBulkFill = async () => {
    const totalLessons = courses.reduce((a, c) => a + (c.lesson_count || 0), 0);
    const noText = courses.reduce((a, c) => a + (c.empty_lesson_count || 0), 0);
    const noVideo = courses.reduce((a, c) => a + ((c.lesson_count || 0) - (c.video_lesson_count || 0)), 0);
    const noLink = courses.reduce((a, c) => a + ((c.lesson_count || 0) - (c.link_lesson_count || 0)), 0);
    if (noText === 0 && noVideo === 0 && noLink === 0) return toast.info("All lessons already have text, videos, and links");
    if (!window.confirm(`Fill missing content across ${totalLessons} lessons?\n\n${noText} need text · ${noVideo} need videos · ${noLink} need links\n\nRuns on the server — you can close this page and check back later.`)) return;
    try {
      const d = await api("POST", "bulk-fill/start");
      if (d.status === "already_running") {
        toast.info("Bulk fill is already running");
      } else {
        toast.success("Bulk fill started — runs in the background");
      }
      setBulkAllGenning(true);
      setBulkJob(d);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(pollStatus, 4000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const cancelBulkFill = async () => {
    try {
      await api("POST", "bulk-fill/cancel");
      toast.info("Cancelling bulk fill…");
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Recovery
  const pollRecovery = useCallback(async () => {
    try {
      const s = await api("GET", "recovery/status");
      setRecoveryStatus(s);
      if (!s.running && s.done) {
        if (recoveryPollRef.current) { clearInterval(recoveryPollRef.current); recoveryPollRef.current = null; }
        load();
      }
    } catch { /* ignore */ }
  }, [load]);

  useEffect(() => {
    // Check recovery status on mount
    api("GET", "recovery/status").then(s => { if (s.running) { setRecoveryStatus(s); recoveryPollRef.current = setInterval(pollRecovery, 4000); } }).catch(() => {});
    return () => { if (recoveryPollRef.current) { clearInterval(recoveryPollRef.current); recoveryPollRef.current = null; } };
  }, [pollRecovery]);

  const startRecovery = async () => {
    if (!window.confirm("Recover text content for damaged lessons?\n\nThis finds lessons that only have links (no text) and regenerates their text content using AI. Existing links/videos are preserved.")) return;
    try {
      await api("POST", "recovery/start");
      toast.success("Recovery started — runs in background");
      setRecoveryStatus({ running: true, total: 0, processed: 0, fixed: 0, errors: 0, done: false, current: "Scanning..." });
      recoveryPollRef.current = setInterval(pollRecovery, 4000);
    } catch (e) { toast.error(e.message); }
  };

  const stopRecovery = async () => {
    try {
      await api("POST", "recovery/stop");
      toast.info("Stopping recovery…");
    } catch (e) { toast.error(e.message); }
  };

  // Scoped fill (per module or per sub-module)
  const pollScopedFill = useCallback(async () => {
    try {
      const s = await api("GET", "scoped-fill/status");
      setScopedFill(s);
      if (!s.running && s.done) {
        if (scopedPollRef.current) { clearInterval(scopedPollRef.current); scopedPollRef.current = null; }
        load();
      }
    } catch { /* ignore */ }
  }, [load]);

  useEffect(() => {
    api("GET", "scoped-fill/status").then(s => { if (s.running) { setScopedFill(s); scopedPollRef.current = setInterval(pollScopedFill, 4000); } }).catch(() => {});
    return () => { if (scopedPollRef.current) { clearInterval(scopedPollRef.current); scopedPollRef.current = null; } };
  }, [pollScopedFill]);

  const startScopedFill = async (courseId, title) => {
    if (scopedFill?.running) return toast.info("A fill is already running");
    if (!window.confirm(`Fill missing content for "${title}"?\n\nAdds text, videos, and links only where missing. Existing content is preserved.`)) return;
    try {
      await api("POST", "scoped-fill/start", { course_id: courseId });
      toast.success(`Filling "${title}" in background…`);
      setScopedFill({ running: true, total: 0, processed: 0, scope: title, current: "Scanning..." });
      scopedPollRef.current = setInterval(pollScopedFill, 4000);
    } catch (e) { toast.error(e.message); }
  };

  const stopScopedFill = async () => {
    try {
      await api("POST", "scoped-fill/stop");
      toast.info("Stopping fill…");
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    if (bulkAllGenning && !pollRef.current) {
      pollRef.current = setInterval(pollStatus, 4000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [bulkAllGenning, pollStatus]);

  const addManual = async () => {
    if (!newTitle.trim()) return toast.error("Enter a module title");
    setCreating(true);
    try { await api("POST", "modules", { title: newTitle.trim() }); setNewTitle(""); load(); toast.success("Module created"); }
    catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the module you want AI to build");
    setAiBuilding(true); setAiProgress("Generating module outline…");
    try {
      const d = await api("POST", "ai/program", { prompt: aiPrompt.trim() });
      setAiProgress("Generating lessons…");
      for (const mod of d.modules || []) {
        setAiProgress(`Building: ${mod.title}`);
        await api("POST", "ai/expand", { module_id: mod.id, module_title: mod.title });
      }
      setAiPrompt(""); toast.success("Module generated"); load();
    } catch (e) { toast.error(e.message); }
    finally { setAiBuilding(false); setAiProgress(""); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its contents?`)) return;
    try { await api("DELETE", `modules/${c.id}`); toast.success("Module deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (openCourse) return <CourseBuilder courseId={openCourse} onBack={() => setOpenCourse(null)} />;

  return (
    <div data-testid="admin-courses">
      {/* manual create */}
      <div className="rp-foam bg-white/70 p-5 mb-6">
        <label className={labelCls}>Create module manually</label>
        <div className="flex gap-2">
          <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && addManual()}
                 placeholder="Module title — e.g. 01: Introduction" className={inputCls} data-testid="course-new-title" />
          <button onClick={addManual} disabled={creating} data-testid="course-add-btn"
                  className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
            <Plus size={14} /> {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </div>

      {/* AI generate */}
      <div className="rp-foam bg-white/55 p-5 mb-6">
        <label className={labelCls}>Generate with AI</label>
        <div className="flex gap-2">
          <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && aiGenerate()}
                 placeholder="Describe the module to build…" className={inputCls} data-testid="ai-course-prompt" />
          <button onClick={aiGenerate} disabled={aiBuilding} data-testid="ai-course-build-btn"
                  className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
            <Sparkles size={14} /> {aiBuilding ? "Building…" : "Build"}
          </button>
        </div>
        {aiBuilding && aiProgress && <p className="mt-2 text-xs text-[#B5793F] flex items-center gap-2"><RefreshCw size={11} className="animate-spin" /> {aiProgress}</p>}
      </div>

      {/* whitelist toggle */}
      <div className="flex justify-end mb-4 gap-3">
        <button onClick={() => setShowWhitelist((s) => !s)} data-testid="toggle-whitelist"
                className="inline-flex items-center gap-2 text-xs font-medium text-[#5C5042] border border-[#26201620] px-3 py-1.5 rounded-full hover:border-[#262016]">
          <Settings2 size={12} /> {showWhitelist ? "Hide" : "Manage"} YouTube channels
        </button>
      </div>
      {showWhitelist && <WhitelistManager />}

      <div className="mb-6">
        <ContentRefreshPanel />
      </div>

      {/* Bulk fill all modules — server-side */}
      {courses && courses.length > 0 && (
        <div className="rp-foam bg-white/70 p-5 mb-8" data-testid="bulk-fill-all-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-medium text-[#262016]">Bulk Fill — Generate missing content across all modules</p>
              <p className="text-[11px] text-[#8A7E6B] mt-1">
                Adds missing text, videos, and links incrementally — safe to run multiple times
                {(() => {
                  const totalLessons = courses.reduce((a, c) => a + (c.lesson_count || 0), 0);
                  const noText = courses.reduce((a, c) => a + (c.empty_lesson_count || 0), 0);
                  const noVideo = courses.reduce((a, c) => a + ((c.lesson_count || 0) - (c.video_lesson_count || 0)), 0);
                  const noLink = courses.reduce((a, c) => a + ((c.lesson_count || 0) - (c.link_lesson_count || 0)), 0);
                  if (noText === 0 && noVideo === 0 && noLink === 0) return " · all lessons complete ✓";
                  return ` · ${noText} need text · ${noVideo} need videos · ${noLink} need links`;
                })()}
              </p>
            </div>
            <div className="flex gap-2">
              {bulkAllGenning ? (
                <button onClick={cancelBulkFill} data-testid="bulk-fill-all-cancel"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-red-400 text-red-600 hover:bg-red-50">
                  <XCircle size={14} /> Stop
                </button>
              ) : (
                <button onClick={startBulkFill} disabled={false} data-testid="bulk-fill-all-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
                  <Sparkles size={14} /> Fill all modules with AI
                </button>
              )}
            </div>
          </div>
          {bulkAllGenning && (
            <div className="mt-3 flex items-center gap-2 text-[13px] text-[#B5793F]" data-testid="bulk-fill-all-progress">
              <RefreshCw size={13} className="animate-spin" />
              <span>{bulkAllProgress || "Starting…"}</span>
            </div>
          )}
          {bulkJob && bulkJob.status === "done" && (
            <div className="mt-3 text-[13px] text-green-700 flex items-center gap-2" data-testid="bulk-fill-done">
              <Check size={13} />
              <span>Complete — {bulkJob.text_done || 0} text, {bulkJob.video_done || 0} videos, {bulkJob.link_done || 0} links generated{bulkJob.errors > 0 ? ` · ${bulkJob.errors} errors` : ""}</span>
            </div>
          )}
          {bulkJob && bulkJob.status === "cancelled" && (
            <div className="mt-3 text-[13px] text-amber-600">
              Stopped — {bulkJob.text_done || 0} text, {bulkJob.video_done || 0} videos generated so far
            </div>
          )}
          {bulkJob && bulkJob.status === "error" && (
            <div className="mt-3 text-[13px] text-red-600">
              Error: {bulkJob.error_msg || "Unknown error"}
            </div>
          )}
        </div>
      )}

      {/* Recovery Tool — regenerate text for damaged lessons */}
      {courses && courses.length > 0 && (
        <div className="bg-amber-50/70 border border-amber-300 rounded-2xl p-5 mb-8" data-testid="recovery-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-medium text-amber-900 flex items-center gap-2"><AlertTriangle size={14} /> Recovery — Regenerate missing text</p>
              <p className="text-[11px] text-amber-700 mt-1">
                Finds lessons that only have links/videos but no text content, and regenerates text using AI. Preserves existing links and videos.
              </p>
            </div>
            <div className="flex gap-2">
              {recoveryStatus?.running ? (
                <button onClick={stopRecovery} data-testid="recovery-stop-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-red-400 text-red-600 hover:bg-red-50">
                  <XCircle size={14} /> Stop
                </button>
              ) : (
                <button onClick={startRecovery} data-testid="recovery-start-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-full">
                  <Wrench size={14} /> Recover Text
                </button>
              )}
            </div>
          </div>
          {recoveryStatus?.running && (
            <div className="mt-3 flex items-center gap-2 text-[13px] text-amber-700" data-testid="recovery-progress">
              <RefreshCw size={13} className="animate-spin" />
              <span>{recoveryStatus.current || `${recoveryStatus.processed || 0}/${recoveryStatus.total || "?"}`}</span>
            </div>
          )}
          {recoveryStatus?.done && !recoveryStatus?.running && (
            <div className="mt-3 text-[13px] text-green-700 flex items-center gap-2" data-testid="recovery-done">
              <Check size={13} />
              <span>Recovery complete — {recoveryStatus.fixed || 0} lessons fixed{recoveryStatus.errors > 0 ? `, ${recoveryStatus.errors} errors` : ""}</span>
            </div>
          )}
        </div>
      )}

      {/* Scoped Fill progress */}
      {scopedFill?.running && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6" data-testid="scoped-fill-progress">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">Filling: {scopedFill.scope}</p>
              <p className="text-[12px] text-blue-600 mt-1 flex items-center gap-2">
                <RefreshCw size={12} className="animate-spin" />
                {scopedFill.current || `${scopedFill.processed || 0}/${scopedFill.total || "?"}`}
              </p>
            </div>
            <button onClick={stopScopedFill} data-testid="scoped-fill-stop-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border border-red-400 text-red-600 hover:bg-red-50">
              <XCircle size={12} /> Stop
            </button>
          </div>
        </div>
      )}
      {scopedFill?.done && !scopedFill?.running && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6" data-testid="scoped-fill-done">
          <p className="text-[13px] text-green-700 flex items-center gap-2">
            <Check size={13} />
            {scopedFill.scope}: {scopedFill.text_done || 0} text, {scopedFill.video_done || 0} videos, {scopedFill.link_done || 0} links{scopedFill.errors > 0 ? ` · ${scopedFill.errors} errors` : ""}
          </p>
        </div>
      )}

      {/* module list */}
      {/* Export / Import / Seed */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button onClick={async () => {
          if (!window.confirm("Seed the full Creative Design curriculum (23 modules, 282 sub-modules, 1502 lessons)?\n\nThis adds the curriculum alongside any existing modules.")) return;
          try {
            toast.info("Seeding curriculum — this may take a minute...");
            const r = await fetch(`${API}/api/admin/lms/seed-curriculum`, { method: "POST", credentials: "include" });
            if (!r.ok) throw new Error("Seed failed");
            const result = await r.json();
            toast.success(`Seeded! ${result.modules} modules, ${result.submodules} sub-modules, ${result.lessons} lessons`);
            load();
          } catch (e) { toast.error(e.message); }
        }} data-testid="lms-seed-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium text-white bg-[#B5793F] hover:bg-[#9A6735]">
          <Sparkles size={14} /> Seed full curriculum
        </button>
        <button onClick={async () => {
          try {
            const r = await fetch(`${API}/api/admin/lms/export`, { credentials: "include" });
            if (!r.ok) throw new Error("Export failed");
            const data = await r.json();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "lms_export.json"; a.click();
            URL.revokeObjectURL(url);
            toast.success(`Exported ${data.modules?.length || 0} modules`);
          } catch (e) { toast.error(e.message); }
        }} data-testid="lms-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium text-[#262016] border border-[#26201625] hover:border-[#B5793F] bg-white/70">
          <Download size={14} /> Export all data
        </button>
        <label data-testid="lms-import-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium text-[#262016] border border-[#26201625] hover:border-[#B5793F] bg-white/70 cursor-pointer">
          <Upload size={14} /> Import data
          <input type="file" accept=".json" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const text = await file.text();
              const data = JSON.parse(text);
              if (!data.modules?.length) return toast.error("No modules found in file");
              if (!window.confirm(`Import ${data.modules.length} modules, ${data.submodules?.length || 0} sub-modules, ${data.lessons?.length || 0} lessons?\n\nThis will REPLACE all existing content.`)) return;
              const r = await fetch(`${API}/api/admin/lms/import`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
              });
              if (!r.ok) throw new Error("Import failed");
              const result = await r.json();
              toast.success(`Imported ${result.modules} modules, ${result.submodules} sub-modules, ${result.lessons} lessons`);
              load();
            } catch (err) { toast.error(err.message); }
            e.target.value = "";
          }} />
        </label>
      </div>

      <div data-testid="admin-courses-list">
        {!courses && <p className="rp-display rp-italic text-[#5C5042]">Loading modules…</p>}
        {courses && courses.length === 0 && <p className="text-sm text-[#8A7E6B]">No modules yet — create your first above.</p>}
        {(courses || []).map((c, idx) => (
          <div key={c.id} className="bg-white rounded-2xl flex items-center gap-4 p-4 md:px-6 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "24px" }} data-testid={`admin-course-${c.id}`}>
            <div className="flex-1 min-w-0">
              <p className="rp-display text-base md:text-lg font-medium text-[#262016] truncate">{c.title}</p>
              <p className="text-xs text-[#8A7E6B]">
                {c.submodule_count || 0} sub-module{c.submodule_count === 1 ? "" : "s"} · {c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"}
                {c.empty_lesson_count > 0 ? <span className="text-red-500"> · {c.empty_lesson_count} no text</span> : ""}
                {c.lesson_count > 0 && (
                  <>
                    {" · "}
                    <span className={c.video_lesson_count === c.lesson_count ? "text-green-600" : "text-[#8A7E6B]"}>
                      {c.video_lesson_count || 0}/{c.lesson_count} videos
                    </span>
                    {" · "}
                    <span className={c.link_lesson_count === c.lesson_count ? "text-green-600" : "text-[#8A7E6B]"}>
                      {c.link_lesson_count || 0}/{c.lesson_count} links
                    </span>
                  </>
                )}
                {" · "}
                <span className={c.published !== false ? "text-[#B5793F] font-semibold" : ""}>{c.published !== false ? "Live" : "Draft"}</span>
              </p>
            </div>
            <button onClick={() => startScopedFill(c.id, c.title)} data-testid={`admin-course-fill-${c.id}`}
                    disabled={scopedFill?.running}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold border border-[#B5793F] text-[#B5793F] hover:bg-[#B5793F]/10 disabled:opacity-40"
                    title="Fill missing text, videos, links for this module">
              <Sparkles size={13} /> Fill
            </button>
            <button onClick={() => setOpenCourse(c.id)} data-testid={`admin-course-open-${c.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-[#F6F1E5]" style={btnPrimary}>
              Open builder
            </button>
            <button onClick={() => remove(c)} data-testid={`admin-course-delete-${c.id}`}
                    className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
